import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';

import {
  CreatePosConfigurationPayload,
  PosConfiguration,
  PosType,
  UpdatePosConfigurationPayload,
} from './_types';

// Query keys for React Query
export const posConfigurationsQueryKey = 'pos-configurations';

export const posConfigurationQueryKey = (id: string) => [
  'pos-configuration',
  id,
];

// Fetch all POS configurations
export const fetchPosConfigurations = async (): Promise<{
  posConfigurations: PosConfiguration[];
  total: number;
}> => {
  const response = await api.get('/api/v4/pos_configurations');
  return {
    posConfigurations: response.data.pos_configurations.map((config: any) =>
      camelcaseKeys(config)
    ),
    total: response.data.pos_configurations.length,
  };
};

// Fetch a single POS configuration
export const fetchPosConfiguration = async (
  id: PosConfiguration['id'],
  posType?: PosType
): Promise<PosConfiguration> => {
  const response = await api.get(`/api/v4/pos_configurations/${id}`, {
    params: {
      ...(posType && { pos_type: posType }),
    },
  });
  return camelcaseKeys(response.data.pos_configuration);
};

// Create a new POS configuration
export const createPosConfiguration = async (
  payload: CreatePosConfigurationPayload
): Promise<PosConfiguration> => {
  const response = await api.post('/api/v4/pos_configurations', {
    pos_configuration: {
      name: payload.name,
      pos_type: payload.posType,
      config: payload.config,
      platform_id: payload.platformId,
    },
  });
  return camelcaseKeys(response.data.pos_configuration);
};

// Update an existing POS configuration
export const updatePosConfiguration = async (
  payload: UpdatePosConfigurationPayload
): Promise<PosConfiguration> => {
  const updateData: any = {};
  if (payload.name !== undefined) updateData.name = payload.name;
  if (payload.posType !== undefined) updateData.pos_type = payload.posType;
  if (payload.config !== undefined) updateData.config = payload.config;

  const response = await api.put(`/api/v4/pos_configurations/${payload.id}`, {
    pos_configuration: updateData,
  });
  return camelcaseKeys(response.data);
};

// Delete a POS configuration
export const deletePosConfiguration = async (
  id: PosConfiguration['id']
): Promise<void> => {
  await api.delete(`/api/v4/pos_configurations/${id}`);
};

// Validate POS configuration PIN
export const setupPosConfigurationPin = async (
  pin: string,
  userId: string,
  password?: string
) => {
  const payload = {
    user_id: userId,
    password,
    kiosk_pin: pin,
  };

  try {
    const response = await api.post('/api/v4/kiosk_pins/update_pin', payload);

    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      return { valid: false, error: 'Invalid PIN' };
    }
    throw error;
  }
};

// Validate POS configuration PIN
export const validatePosConfigurationPin = async (
  posType: PosType,
  pin: string,
  userId?: string
): Promise<{ valid: boolean; error?: string }> => {
  try {
    const payload: { pin: string; user_id?: string; pos_type: PosType } = {
      pin,
      pos_type: posType,
    };

    if (userId) {
      payload.user_id = userId;
    }

    await api.post(`/api/v4/pos_configurations/validate_pin`, payload);

    return { valid: true };
  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      return { valid: false, error: 'Invalid PIN' };
    }
    throw error;
  }
};
